import { Box, Button, Divider, Flex, Heading, Image, Text } from "@chakra-ui/react";
import { useContractFunction, useEthers } from "@usedapp/core";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { useHelperContract, useMarketplaceContract } from "../../../core/hooks/useContract";
import { IMoralisNft } from "../../../core/models/nft";
import { Quote } from "../../../core/models/quote";
import { formatNumber } from "../../../core/utils/number";
import { quoteService } from "../../../services/quote.service";
import { useAppSelector } from "../../../store/hook";
import BigNumber from "bignumber.js";

interface Props {
  nft: IMoralisNft | undefined;
  quote: Quote;
  onSuccess: () => void;
}

export const CancelListingForm = React.memo(({ nft, quote, onSuccess }: Props) => {
  const { account } = useEthers();
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const token = useMemo(
    () => supportedTokens.byId[quote.take.asset_type.token_address],
    [quote.take.asset_type.token_address, supportedTokens.byId]
  );
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);

  const helperContract = useHelperContract();
  const marketplaceContract = useMarketplaceContract();
  const cancelFunc = useContractFunction(marketplaceContract as any, "cancel");

  useEffect(() => {
    async function delist() {
      if (!quote.id) return;
      await quoteService.delist(quote?.id);
      onSuccess();
      setIsLoading(false);
    }
    if (cancelFunc.state.status === "Success") {
      delist();
    }
    if (cancelFunc.state.status === "Exception" || cancelFunc.state.status === "Fail") {
      setIsLoading(false);
    }
  }, [cancelFunc.state]);

  const onCancelListing = async () => {
    try {
      setIsLoading(true);
      const onChainQuote = await Quote.toOnChainQuote(quote, helperContract);
      cancelFunc.send(onChainQuote);
    } catch (e) {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box>
        <Heading size="md">{t("Price")}</Heading>
        <Flex marginTop="12px">
          <Flex background="#f8f8f8" borderRadius="15px" padding="8px 24px" alignItems="center">
            <Image src={token?.logoUrl} height="26px" width="26px" marginRight="8px" />
            <Text fontWeight="700" fontSize={"26px"}>
              <Text as="span">
                {formatNumber(new BigNumber(quote?.take.value || 0).div(Math.pow(10, token.decimals)).toNumber())}
              </Text>
            </Text>
          </Flex>
        </Flex>
      </Box>

      <Box padding="24px 0px">
        <Divider />
      </Box>

      <Button colorScheme="red" width="150px" onClick={onCancelListing} isLoading={isLoading}>
        {t("Delist")}
      </Button>
    </Box>
  );
});
